import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysDeliveryNote } from './queryKeys';

type DeleteTemplateResponse = {
  code: number;
  id: UUID;
  requestId: string;
};

type DeleteTemplatePayload = {
  companyId?: UUID;
  templateId: UUID;
};

/**
 * Updates a template.
 *
 * @param {DeleteTemplatePayload} templateData - An object containing the template details to be updated.
 * @returns {Promise<DeleteTemplateResponse>} - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Template/operation/delete_dln_template_by_id_dln_template_delete_delete
 */
export const deleteTemplate = async (templateData: DeleteTemplatePayload) => {
  const response = await vestigasApi
    .delete(ENDPOINT.DELIVERY_NOTE.DELETE_TEMPLATE(), {
      searchParams: templateData,
    })
    .json<DeleteTemplateResponse>();

  return response;
};

/**
 * Custom hook for updating a template using mutations.
 *
 * @returns {Object} An object containing the mutation function and other properties.
 */
export const useMutationDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (templateData: DeleteTemplatePayload) =>
      deleteTemplate(templateData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeysDeliveryNote.getTemplates(),
      });
    },
  });
};
